import { Injector, ModuleWithProviders, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Library, Loader, LoaderOptions } from '@googlemaps/js-api-loader';
import { GoogleMapsLoaderOptions } from './google-maps.config';
import { GOOGLE_MAPS_PROVIDER } from './google-maps.provider';
import {
  GOOGLE_MAPS_LIBRARY,
  GOOGLE_MAPS_LOADER,
} from './google-maps-loader.provider';

@NgModule({
  imports: [CommonModule],
})
export class SharedGoogleMapsUtilModule {
  constructor(private _injector: Injector) {
    const libraryToLoad = this._injector.get(GOOGLE_MAPS_LIBRARY, null);
    const loader = this._injector.get(GOOGLE_MAPS_LOADER);

    if (!libraryToLoad) {
      return;
    }

    loader.importLibrary(
      // assert parameter type to avoid matching multiple `importLibrary(name)` overloads
      libraryToLoad as Parameters<typeof loader.importLibrary>[0],
    );
  }

  /**
   * Module to be imported in root app module
   *
   * Provide a value for `GOOGLE_MAPS` injection token, for accessing the global `google`
   * object.
   *
   * With `options` passed, provide a value for the `GOOGLE_MAPS_LOADER` injection token:
   * an instance of the JS API Loader, for lazy-loading the Maps Javascript API.
   *
   * @param options Loader options, for lazy-loading the Maps Javascript API
   * @returns Module with providers
   */
  static forRoot({
    options,
  }: {
    options: GoogleMapsLoaderOptions;
  }): ModuleWithProviders<SharedGoogleMapsUtilModule> {
    return {
      ngModule: SharedGoogleMapsUtilModule,
      providers: [
        GOOGLE_MAPS_PROVIDER,
        {
          provide: GOOGLE_MAPS_LOADER,
          useValue: new Loader(options as LoaderOptions),
        },
      ],
    };
  }

  /**
   * Module with Maps Javascript API lazy-loaded
   *
   * Lazy-loads the Maps Javascript API for the given `library`. Provide the
   * `GOOGLE_MAPS_LIBRARY` injection token with `library` as the value.
   *
   * @param library Library to lazy-load for the Maps Javascript API
   * @returns Module with providers
   */
  static forChild({
    library,
  }: {
    library: Library;
  }): ModuleWithProviders<SharedGoogleMapsUtilModule> {
    return {
      ngModule: SharedGoogleMapsUtilModule,
      providers: [
        {
          provide: GOOGLE_MAPS_LIBRARY,
          useValue: library,
        },
      ],
    };
  }
}
